import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, ChevronRight, Heart } from 'lucide-react'
import { carePlans } from '../data/carePlans'
import { specialties } from '../data/specialties'

const SearchBox = ({ placeholder = 'Search conditions, specialties, or care plans...', className = '' }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [activeSuggestion, setActiveSuggestion] = useState(-1)
  const navigate = useNavigate()
  const searchRef = useRef(null)
  const suggestionsRef = useRef(null)

  // Generate suggestions based on search term
  useEffect(() => {
    if (searchTerm.trim().length > 1) {
      const lowercaseQuery = searchTerm.toLowerCase()
      const results = []

      // Search care plans
      const matchingCarePlans = carePlans.filter(plan =>
        plan.title.toLowerCase().includes(lowercaseQuery) ||
        plan.description.toLowerCase().includes(lowercaseQuery) ||
        plan.category.toLowerCase().includes(lowercaseQuery)
      ).slice(0, 5).map(plan => ({
        ...plan,
        type: 'care-plan',
        displayName: plan.title
      }))

      // Search specialties
      const matchingSpecialties = specialties.filter(specialty =>
        specialty.name.toLowerCase().includes(lowercaseQuery) ||
        specialty.description.toLowerCase().includes(lowercaseQuery)
      ).slice(0, 3).map(specialty => ({
        ...specialty,
        type: 'specialty',
        displayName: specialty.name
      }))

      // Combine results, prioritize exact matches
      results.push(...matchingCarePlans, ...matchingSpecialties)

      // Sort by relevance (exact matches first, then alphabetical)
      results.sort((a, b) => {
        const aName = a.displayName.toLowerCase()
        const bName = b.displayName.toLowerCase()
        const aExact = aName.startsWith(lowercaseQuery)
        const bExact = bName.startsWith(lowercaseQuery)

        if (aExact && !bExact) return -1
        if (!aExact && bExact) return 1
        return aName.localeCompare(bName)
      })

      setSuggestions(results.slice(0, 8)) // Limit to 8 suggestions
      setShowSuggestions(true)
    } else {
      setSuggestions([])
      setShowSuggestions(false)
    }
    setActiveSuggestion(-1)
  }, [searchTerm])

  // Handle form submission
  const handleSearch = (e) => {
    e.preventDefault()
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm)}`)
      setShowSuggestions(false)
    }
  }

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    if (suggestion.type === 'care-plan') {
      navigate(`/care-plan/${suggestion.id}`)
    } else {
      navigate(`/specialty/${suggestion.id}`)
    }
    setSearchTerm('')
    setShowSuggestions(false)
  }

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActiveSuggestion(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setActiveSuggestion(prev => prev > 0 ? prev - 1 : -1)
        break
      case 'Enter':
        e.preventDefault()
        if (activeSuggestion >= 0) {
          handleSuggestionClick(suggestions[activeSuggestion])
        } else {
          handleSearch(e)
        }
        break
      case 'Escape':
        setShowSuggestions(false)
        setActiveSuggestion(-1)
        break
    }
  }

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false)
        setActiveSuggestion(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Highlight matching text in suggestions
  const highlightMatch = (text, query) => {
    if (!query) return text

    const regex = new RegExp(`(${query})`, 'gi')
    const parts = text.split(regex)

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} style={{ backgroundColor: '#fff3cd', fontWeight: 'bold' }}>
          {part}
        </span>
      ) : part
    )
  }

  return (
    <div className={`search-container ${className}`} ref={searchRef}>
      <form onSubmit={handleSearch}>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            className="search-input"
            placeholder={placeholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (suggestions.length > 0) setShowSuggestions(true)
            }}
          />
          <Search
            size={20}
            style={{
              position: 'absolute',
              right: '1rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666',
              pointerEvents: 'none'
            }}
          />
        </div>
      </form>

      {showSuggestions && suggestions.length > 0 && (
        <div
          className="search-suggestions"
          ref={suggestionsRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #e0e0e0',
            borderTop: 'none',
            borderRadius: '0 0 8px 8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            zIndex: 1000,
            maxHeight: '400px',
            overflowY: 'auto'
          }}
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={`${suggestion.type}-${suggestion.id}`}
              className={`search-suggestion-item ${index === activeSuggestion ? 'active' : ''}`}
              onClick={() => handleSuggestionClick(suggestion)}
              style={{
                padding: '12px 16px',
                cursor: 'pointer',
                borderBottom: index < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                backgroundColor: index === activeSuggestion ? '#f8f9fa' : 'white',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={() => setActiveSuggestion(index)}
            >
              <div style={{ flexShrink: 0 }}>
                {suggestion.type === 'specialty' ? (
                  <suggestion.icon size={18} color="#00bcd4" />
                ) : (
                  <Heart size={18} color="#666" />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontWeight: '500',
                  fontSize: '14px',
                  marginBottom: '2px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: '#1e293b'
                }}>
                  {highlightMatch(suggestion.displayName, searchTerm)}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#64748b',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {suggestion.type === 'care-plan' ? suggestion.category : 'Specialty'}
                </div>
              </div>
              <div style={{ flexShrink: 0 }}>
                <ChevronRight size={16} color="#ccc" />
              </div>
            </div>
          ))}

          {suggestions.length === 8 && (
            <div
              style={{
                padding: '8px 16px',
                textAlign: 'center',
                fontSize: '12px',
                color: '#00bcd4',
                cursor: 'pointer',
                borderTop: '1px solid #f0f0f0',
                backgroundColor: '#f8f9fa',
                fontWeight: '500'
              }}
              onClick={handleSearch}
            >
              View all results for "{searchTerm}"
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SearchBox
